/**
 * "Only Verified people can be put on a show" (onboarding, 2026-10-08).
 *
 * Every assign endpoint in the backend answers 409 with
 * `{ code: 'CONTRACTOR_NOT_VERIFIED', message, contractors: [{ id, name }] }`.
 * The screens that assign people are many (Session Management, Assign Contractor,
 * opportunities, …) and each one shows its own generic error dialog, so this is
 * handled once, where every request passes: the generic dialog is replaced by one
 * that offers "Verify now" (opens Manage Approvals for that person).
 *
 * The replacement runs on the next tick on purpose: the calling action dispatches its
 * own error dialog right after the request returns, and the later dispatch wins.
 */
import { store } from "../store/store";
import { ALERT_DIALOG } from "../store/constants/auth";
// The constant, not the action creator: the actions module imports apiService, which
// imports this file, and a plain object keeps that import graph free of cycles.
import { OPEN_MANAGE_APPROVALS_MODAL } from "../store/constants/config/contractorLink";

export const CONTRACTOR_NOT_VERIFIED = "CONTRACTOR_NOT_VERIFIED";

interface NotVerifiedBody {
  code?: string;
  message?: string;
  contractors?: Array<{ id: string; name: string }>;
}
type HttpErrorShape = { response?: { status?: number; data?: NotVerifiedBody } };

export function isNotVerifiedError(err: unknown): boolean {
  const e = err as HttpErrorShape | null;
  return e?.response?.status === 409 && e?.response?.data?.code === CONTRACTOR_NOT_VERIFIED;
}

export function showVerifyFirstDialog(err: unknown): void {
  const data: NotVerifiedBody = (err as HttpErrorShape | null)?.response?.data || {};
  const people = Array.isArray(data.contractors) ? data.contractors : [];
  const state = store.getState() as { auth?: { activeRole?: string; userRole?: string } };
  const auth = state?.auth || {};
  const role = String(auth.activeRole || auth.userRole || "").toLowerCase();
  // Only the team can verify. A Freelance EC booking someone, or a contractor accepting
  // a show, reads the message, which says who can verify.
  const canVerify = ["admin", "superadmin", "coordinator"].includes(role);
  const first = canVerify ? people[0] : null;
  setTimeout(() => {
    store.dispatch({
      type: ALERT_DIALOG,
      payload: {
        open: true,
        title: canVerify ? "Verify first" : "Not verified yet",
        message:
          data.message ||
          "This person is not verified yet. A coordinator or admin needs to verify the profile before booking.",
        okLabel: first ? "Verify now" : "Ok",
        closeLabel: "Close",
        onOk: first
          ? () =>
              store.dispatch({
                type: OPEN_MANAGE_APPROVALS_MODAL,
                payload: { contractorId: first.id },
              })
          : undefined,
        isDangerouslySetHtml: false,
      },
    });
  }, 0);
}
